import React from 'react';
import { SpreadsheetData } from '../types';
import { createEmptySpreadsheetData, ensureDataDimensions } from '../utils';

interface UseSpreadsheetDataProps {
  data: SpreadsheetData[];
  rows: number;
  columns: number;
  calculatedRows: number;
  calculatedColumns: number;
  autoFillSpace: boolean;
  onDataChange?: (data: SpreadsheetData[]) => void;
}

export function useSpreadsheetData({
  data,
  rows,
  columns,
  calculatedRows,
  calculatedColumns,
  autoFillSpace,
  onDataChange
}: UseSpreadsheetDataProps) {
  // Initialize spreadsheet data
  const [spreadsheetData, setSpreadsheetData] = React.useState<SpreadsheetData[]>(() => {
    const currentColumns = autoFillSpace ? calculatedColumns : columns;
    const currentRows = autoFillSpace ? calculatedRows : rows;
    
    if (data.length > 0) {
      const filledData = ensureDataDimensions(data, currentRows, currentColumns);
      return filledData;
    }
    
    return createEmptySpreadsheetData(currentRows, currentColumns);
  });

  // Update spreadsheet data when props change
  React.useEffect(() => {
    const currentColumns = autoFillSpace ? calculatedColumns : columns;
    const currentRows = autoFillSpace ? calculatedRows : rows;
    
    if (data.length > 0) {
      setSpreadsheetData(prevData => {
        // Check if the data content has changed
        const dataChanged = JSON.stringify(data) !== JSON.stringify(prevData.slice(0, data.length));
        
        if (dataChanged) {
          const filledData = ensureDataDimensions(data, currentRows, currentColumns);
          return filledData;
        } else {
          // Data hasn't changed, just ensure dimensions are correct
          const updatedData = ensureDataDimensions(prevData, currentRows, currentColumns);
          return updatedData;
        }
      });
    } else {
      // Create empty data with current dimensions
      const emptyData = createEmptySpreadsheetData(currentRows, currentColumns);
      setSpreadsheetData(emptyData);
    }
  }, [data, rows, columns, calculatedRows, calculatedColumns, autoFillSpace]);

  const handleRowsChange = React.useCallback((newRows: SpreadsheetData[]) => {
    // Ensure we're not losing data by validating the change
    if (newRows && Array.isArray(newRows)) {
      setSpreadsheetData(newRows);
      if (onDataChange) {
        onDataChange(newRows);
      }
    } else {
      console.warn('Invalid data received in handleRowsChange:', newRows);
    }
  }, [onDataChange]);

  return {
    spreadsheetData,
    handleRowsChange
  };
} 